import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Button, FormControl, InputLabel, Select, MenuItem, Typography,
} from '@mui/material'
import { ForkRight } from '@mui/icons-material'

export default function CreateScenarioDialog({
  open,
  onClose,
  onCreate,
  approvedPlan,
  existingScenarios = [],
}) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [parentType, setParentType] = useState('plan')
  const [parentScenarioId, setParentScenarioId] = useState('')

  const nameError = name.length > 80
    ? 'Name must be 80 characters or fewer'
    : existingScenarios.some((s) => s.name.toLowerCase() === name.trim().toLowerCase())
      ? 'Scenario name must be unique within this plan'
      : ''

  const canCreate = name.trim().length > 0 && !nameError && (parentType === 'plan' || parentScenarioId)

  const handleCreate = () => {
    if (!canCreate) return
    onCreate({
      name: name.trim(),
      description: description.trim(),
      parentType,
      parentId: parentType === 'plan' ? approvedPlan?.id : parentScenarioId,
    })
    setName('')
    setDescription('')
    setParentType('plan')
    setParentScenarioId('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ForkRight color="primary" />
        New Scenario
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: '16px !important' }}>
        <TextField
          label="Scenario Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!nameError}
          helperText={nameError || `${name.length}/80 characters`}
          fullWidth
          autoFocus
          slotProps={{ htmlInput: { maxLength: 80 } }}
        />

        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          rows={3}
          placeholder="Describe the purpose of this scenario..."
        />

        <FormControl fullWidth>
          <InputLabel>Branch From</InputLabel>
          <Select
            value={parentType}
            onChange={(e) => { setParentType(e.target.value); setParentScenarioId('') }}
            label="Branch From"
          >
            <MenuItem value="plan">
              From Approved Plan: {approvedPlan?.name || 'No plan'}
            </MenuItem>
            <MenuItem value="scenario" disabled={existingScenarios.length === 0}>
              From Existing Scenario
            </MenuItem>
          </Select>
        </FormControl>

        {parentType === 'scenario' && (
          <FormControl fullWidth>
            <InputLabel>Source Scenario</InputLabel>
            <Select
              value={parentScenarioId}
              onChange={(e) => setParentScenarioId(e.target.value)}
              label="Source Scenario"
            >
              {existingScenarios.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Typography variant="caption" color="text.secondary">
          The new scenario will copy all data from the selected source. Changes made
          in the scenario will not affect the source until promoted.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleCreate} disabled={!canCreate}>
          Create Scenario
        </Button>
      </DialogActions>
    </Dialog>
  )
}
